import { useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { Linkedin, Link2, ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Motion";
import { BlogVisual } from "../components/BlogVisuals";
import { useContent, LINKS } from "../lib/content";

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const md = {
  h1: () => null, // The H1 is rendered in the page header, hide the one in the body
  h2: (props) => <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-14 mb-5" {...props} />,
  h3: (props) => <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight mt-10 mb-3" {...props} />,
  p: (props) => <p className="text-base text-foreground/80 leading-[1.85] mb-6" {...props} />,
  strong: (props) => <strong className="text-foreground font-semibold" {...props} />,
  em: (props) => <em className="italic text-foreground/85" {...props} />,
  ul: (props) => <ul className="list-disc pl-6 space-y-2 mb-6 text-foreground/80 leading-relaxed" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 space-y-2 mb-6 text-foreground/80 leading-relaxed" {...props} />,
  li: (props) => <li className="pl-1" {...props} />,
  a: (props) => <a className="text-secondary hover:underline underline-offset-4" {...props} />,
  blockquote: (props) => <blockquote className="my-6 border-l-2 border-secondary/50 pl-4 italic text-foreground/70" {...props} />,
  hr: () => <hr className="my-10 border-border" />,
};

// Split content into segments: markdown chunks and [VISUAL n] markers.
function splitByVisuals(content = "") {
  // Remove the block-quoted spec entirely, replace with a marker token.
  // Pattern: '> [VISUAL n — ...]' possibly followed by lines starting with '> ' or ' '.
  const lines = content.split("\n");
  const segments = [];
  let buf = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const m = line.match(/^>\s*\[VISUAL\s*(\d+)/i);
    if (m) {
      if (buf.length) segments.push({ type: "md", value: buf.join("\n") });
      buf = [];
      // Consume this line and any subsequent quote-continuation lines that are part of the spec.
      i++;
      while (i < lines.length && /^(>|\s{2,}\S)/.test(lines[i])) i++;
      // Also consume trailing blank line(s) around the block for cleaner spacing.
      segments.push({ type: "visual", n: parseInt(m[1], 10) });
      continue;
    }
    buf.push(line);
    i++;
  }
  if (buf.length) segments.push({ type: "md", value: buf.join("\n") });
  return segments;
}

export default function BlogPost() {
  const { slug } = useParams();
  const { posts, loaded } = useContent();
  const navigate = useNavigate();
  const post = posts.find((p) => p.slug === slug);
  const idx = posts.findIndex((p) => p.slug === slug);
  const next = idx >= 0 ? posts[(idx + 1) % posts.length] : null;

  const segments = useMemo(() => splitByVisuals(post?.content || ""), [post]);

  useEffect(() => {
    if (!post) return;
    document.title = post.meta_title || `${post.title} - Yash Bhatt`;
    const setMeta = (selector, attr, value) => {
      if (!value) return;
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [name, val] = selector.replace(/[[\]"']/g, "").split("=");
        el.setAttribute(name, val);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", post.meta_description);
    setMeta('meta[property="og:title"]', "content", post.og_title || post.meta_title);
    setMeta('meta[property="og:description"]', "content", post.og_description || post.meta_description);
    setMeta('meta[name="twitter:title"]', "content", post.og_title || post.meta_title);
    setMeta('meta[name="twitter:description"]', "content", post.og_description || post.meta_description);
  }, [post]);

  useEffect(() => {
    if (loaded && !post) navigate("/404", { replace: true });
  }, [loaded, post, navigate]);

  if (!post) return <div className="min-h-screen" />;

  const url = typeof window !== "undefined" ? window.location.href : "";
  const share = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    x: `https://x.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(url)}`,
  };
  const copyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  return (
    <main data-testid="blog-post-page" className="pt-16">
      <header className="relative overflow-hidden border-b border-border">
        <div className="mesh-glow w-[500px] h-[500px] bg-violet-600/15 -top-40 -right-32" aria-hidden="true" />
        <div className="max-w-3xl mx-auto px-6 py-20 md:py-28">
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
              <li><Link to="/" className="hover:text-secondary transition-colors duration-200">Home</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/blog" className="hover:text-secondary transition-colors duration-200">Blog</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-secondary truncate max-w-[200px]">{post.title}</li>
            </ol>
          </nav>
          <MaskedLine>
            <div className="flex items-center gap-3 mb-6">
              <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-medium">{post.category}</span>
              <span className="font-mono text-xs text-muted-foreground">{post.date} · {post.read_time}</span>
            </div>
          </MaskedLine>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.1]">
            <MaskedLine delay={0.15}>{post.title}</MaskedLine>
          </h1>
          <MaskedLine delay={0.3}>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">{post.excerpt}</p>
          </MaskedLine>
          <div className="mt-8 flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mr-1">Share</span>
            <a href={share.linkedin} target="_blank" rel="noreferrer" aria-label="Share on LinkedIn" data-testid="share-linkedin"
              className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-secondary hover:border-secondary/50 transition-colors duration-200"><Linkedin size={14} /></a>
            <a href={share.x} target="_blank" rel="noreferrer" aria-label="Share on X" data-testid="share-x"
              className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-secondary hover:border-secondary/50 transition-colors duration-200"><XIcon /></a>
            <button onClick={copyLink} aria-label="Copy link" data-testid="share-copy"
              className="h-9 w-9 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-secondary hover:border-secondary/50 transition-colors duration-200"><Link2 size={14} /></button>
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-16 md:py-20">
        {segments.map((s, i) =>
          s.type === "md" ? (
            <ReactMarkdown key={i} components={md}>{s.value}</ReactMarkdown>
          ) : (
            <BlogVisual key={i} slug={slug} n={s.n} />
          )
        )}

        <Reveal className="mt-16">
          <div className="rounded-2xl border border-border bg-gradient-to-br from-violet-600/10 via-card to-blue-600/10 p-8 md:p-10">
            <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight">Working through this in your organization?</h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">I advise enterprise teams on exactly these problems. Start with a free 30-minute consultation.</p>
            <a href={LINKS.calendly} target="_blank" rel="noreferrer" data-testid="post-cta-book"
              className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity duration-200">
              <Calendar size={15} /> Discuss Your Challenge
            </a>
          </div>
        </Reveal>

        <div className="mt-12 flex items-center justify-between border-t border-border pt-8">
          <Link to="/blog" data-testid="post-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-secondary transition-colors duration-200">
            <ArrowLeft size={14} /> All articles
          </Link>
          {next && next.slug !== slug && (
            <Link to={`/blog/${next.slug}`} data-testid="post-next-link" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline text-right">
              <span className="hidden sm:inline max-w-[260px] truncate">{next.title}</span>
              <span className="sm:hidden">Next</span> <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </article>
    </main>
  );
}
